/**
 * Puzzle Initialization
 *
 * Core initialization logic for both builder and solver modes,
 * cell event attachment, and solution state loading.
 */

import { appState } from '../state/AppState';
import { Puzzle, HistoryManager, Settings } from '../core';
import { CellEvents } from '../events/CellEvents';
import { MainMenu, SolutionMenu, ClueMenu } from '../ui';

import { session } from './puzzleSession';
import {
    getComponentHtml,
    addOverlayDivs,
    showOverlayDivs,
    addHiddenElements,
    applyColorScheme,
} from './domSetup';
import { showSaveWarning, savePuzzle } from './saveWarning';
import {
    updateMainMenu,
    updateClueBar,
    updateSolutionBar,
    updateHistoryBar,
} from './menuUpdates';
import {
    getCellOperationContext,
    getEditorOperationContext,
} from './operationContexts';
import { createEventContext } from './eventContext';
import { updateClueColumns } from './clueDrag';
import { createMainMenuConfig, createClueMenuConfig } from './menuConfigs';

// Import TypeScript operations
import {
    setColored,
    setBridge,
    setImage,
    setBlack,
    setVerticalLine,
    setHorizontalLine,
    setKnown,
} from '../operations/CellOperations';
import {
    changeDirection,
    toggleSyncEditing,
} from '../operations/EditorOperations';

/**
 * Select the first cell in the puzzle
 */
function selectFirstCell(): void {
    const firstCell = document.getElementById('mezo-0');
    if (firstCell) {
        appState.currentElement = firstCell;
        appState.currentCellId = 0;
        session.cellEvents?.switchToCell(firstCell);
    }
}

/**
 * Set up the history manager with undo/redo support
 */
function setupHistoryManager(isBuilderMode: boolean): void {
    session.historyManager = new HistoryManager(20);
    session.historyManager.setPuzzleXmlGetter(
        () => session.currentPuzzle?.toXml() ?? '',
    );
    session.historyManager.setCellIdGetter(() => appState.currentCellId);
    session.historyManager.setDirectionGetter(() => appState.isHorizontal);
    session.historyManager.setOnUnsaved(() => {
        appState.isSaved = false;
        showOverlayDivs();
    });
    session.historyManager.setOnReload(
        (xml: string, cellId: number, isHorizontal: boolean) => {
            if (!session.currentPuzzle) return;
            try {
                session.currentPuzzle = Puzzle.fromXml(xml);
                const puzzleEl = document.getElementById('rejtveny');
                if (!puzzleEl) return;

                puzzleEl.innerHTML = session.currentPuzzle.toString(
                    appState.clueMarking,
                    appState.isHorizontal,
                    appState.knownMarking,
                    appState.validCharPattern,
                );
                addHiddenElements();
                attachCellEvents();
                appState.currentCellId = cellId;
                appState.isHorizontal = isHorizontal;

                if (isBuilderMode) {
                    updateClueColumns();
                }

                const cellElement = document.getElementById(`mezo-${cellId}`);
                if (cellElement) {
                    appState.currentElement = cellElement;
                    session.cellEvents?.switchToCell(cellElement);
                }
                updateClueBar();
                updateMainMenu();
                applyColorScheme(session.currentPuzzle);
            } catch (e) {
                console.error('Error reloading puzzle:', e);
            }
        },
    );
    session.historyManager.save('Rejtvény betöltése');
}

/**
 * Shared puzzle initialization logic for both builder and solver modes.
 *
 * @param isBuilderMode - Whether initializing in builder mode
 * @param elementId - The container element ID
 * @param id - The puzzle ID (builder) or solution ID (solver)
 */
function initPuzzleBase(
    isBuilderMode: boolean,
    elementId: string,
    id: number,
): void {
    const mode = isBuilderMode ? 'builder' : 'solver';
    console.log(`Initializing puzzle ${mode}: element=${elementId}, id=${id}`);

    // Set mode state
    appState.isBuilderMode = isBuilderMode;
    if (isBuilderMode) {
        appState.puzzleId = id;
    } else {
        appState.solutionId = id;
    }

    // Add overlay divs (wire save warning callback)
    addOverlayDivs(() => showSaveWarning());

    // Get the container and set up component structure
    const container = document.getElementById(elementId);
    if (!container) {
        console.error(`Container element '${elementId}' not found`);
        return;
    }

    container.innerHTML = getComponentHtml();

    // Fetch the puzzle XML
    const fetchParam = isBuilderMode ? `rejtvenyID=${id}` : `fejtesID=${id}`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `/megnyitas?${fetchParam}`, false);
    xhr.send();

    const response = xhr.responseText;
    const parts = response.split('||', 2);

    if (parts.length < 2) {
        const headerEl = document.getElementById('rejtveny_fejlec');
        if (headerEl) {
            headerEl.innerHTML = 'Hiba a rejtvény betöltésekor';
        }
        console.error('Invalid response from server');
        return;
    }

    appState.isVerified = parts[0] === '1';
    const xmlString = parts[1];

    // Solver: fetch actual puzzle ID
    if (!isBuilderMode) {
        const xhrPuzzle = new XMLHttpRequest();
        xhrPuzzle.open('GET', `/rejtveny?fejtesID=${id}`, false);
        xhrPuzzle.send();
        appState.puzzleId = parseInt(xhrPuzzle.responseText, 10);
    }

    // Parse the puzzle
    try {
        session.currentPuzzle = Puzzle.fromXml(xmlString);
    } catch (error) {
        const headerEl = document.getElementById('rejtveny_fejlec');
        const puzzleEl = document.getElementById('rejtveny');
        if (headerEl) {
            headerEl.innerHTML = 'XML hiba';
        }
        if (puzzleEl) {
            puzzleEl.innerHTML = String(error);
        }
        console.error('XML parsing error:', error);
        return;
    }

    // Render the puzzle
    const puzzleEl = document.getElementById('rejtveny');
    if (puzzleEl) {
        puzzleEl.innerHTML = session.currentPuzzle.toString(
            appState.clueMarking,
            appState.isHorizontal,
            appState.knownMarking,
            appState.validCharPattern,
        );
    }

    // Apply color scheme
    applyColorScheme(session.currentPuzzle);

    // Fetch and load settings from /beallitasok endpoint
    const settings = new Settings({
        type: session.currentPuzzle.type,
        subtype: '',
        borderColor: session.currentPuzzle.borderColor,
    });
    settings.fetchAndLoadSync(
        appState.puzzleId,
        session.currentPuzzle.type,
        isBuilderMode,
    );

    // Update appState with settings
    appState.allowColored = settings.allowColored;
    appState.allowBridge = settings.allowBridge;
    appState.allowEmpty = settings.allowEmpty;
    appState.allowBlack = settings.allowBlack;
    appState.allowImages = settings.allowImages;
    appState.allowSolution = settings.allowSolution;
    appState.hasGraphics = session.currentPuzzle.hasMaskImages();
    appState.hasMultipleGrids = session.currentPuzzle.countGrids() > 1;

    // Fetch base64 images for colored and bridge buttons
    const bgColor = session.currentPuzzle.backgroundColor.replace('#', '');

    const coloredXhr = new XMLHttpRequest();
    coloredXhr.open('GET', `/szinesgomb?hatterszin=${bgColor}&betu=M`, false);
    coloredXhr.send();
    appState.coloredBase64 = coloredXhr.responseText;

    const bridgeXhr = new XMLHttpRequest();
    bridgeXhr.open('GET', '/szinesgomb?hatterszin=39c2e4&betu=H', false);
    bridgeXhr.send();
    appState.bridgeBase64 = bridgeXhr.responseText;

    // Initialize history manager
    setupHistoryManager(isBuilderMode);

    // Initialize menus
    session.mainMenu = new MainMenu(createMainMenuConfig(isBuilderMode));

    session.solutionMenu = new SolutionMenu({
        initialSolution: session.currentPuzzle.solution ?? '',
        isAllowed: () => appState.allowSolution,
        ...(isBuilderMode
            ? {
                  onSolutionChange: (solution: string) => {
                      if (session.currentPuzzle) {
                          session.currentPuzzle.solution = solution;
                          appState.isSaved = false;
                          showOverlayDivs();
                      }
                  },
              }
            : {}),
    });

    session.clueMenu = new ClueMenu(
        createClueMenuConfig(
            isBuilderMode
                ? () => {
                      appState.isSaved = false;
                      showOverlayDivs();
                      session.historyManager?.save('Meghatározás módosítás');
                  }
                : undefined,
        ),
    );

    // Render menus
    updateMainMenu();
    updateSolutionBar();
    updateHistoryBar();

    // Add hidden elements
    addHiddenElements();

    // Set up event handlers
    attachCellEvents();

    // Mode-specific post-initialization
    if (!isBuilderMode) {
        loadSolutionState(id);
    }

    // Select first cell
    selectFirstCell();

    // Update clue bar
    updateClueBar();

    // Builder-specific post-initialization
    if (isBuilderMode) {
        updateClueColumns();
        document.addEventListener('puzzle:save', () => savePuzzle());
    }

    console.log(`Puzzle ${mode} initialized successfully`);
}

/**
 * Initialize the puzzle builder.
 *
 * @param elementId - The container element ID
 * @param puzzleId - The puzzle ID to load
 */
export function initPuzzleBuilder(elementId: string, puzzleId: number): void {
    initPuzzleBase(true, elementId, puzzleId);
}

/**
 * Attach cell events to all cells
 */
function attachCellEvents(): void {
    if (!session.currentPuzzle || !session.historyManager) return;

    session.cellEvents = new CellEvents({
        getGrid: (cellId: number) => session.currentPuzzle!.getGrid(cellId)!,
        getCell: (cellId: number) => session.currentPuzzle!.getCell(cellId)!,
        getNextSyncCell: (cellId: number) =>
            session.currentPuzzle!.getNextCell(cellId)!,
        history: session.historyManager,
        context: createEventContext(),
        onSelectionChange: () => {
            updateClueBar();
        },
        onMenuUpdate: () => {
            updateMainMenu();
        },
        onHistoryUpdate: () => {
            updateHistoryBar();
            updateMainMenu();
        },
        onClueUpdate: () => {
            updateClueBar();
            updateClueColumns();
        },
        // Unified callbacks - same functions as menu buttons
        onToggleDirection: (el: HTMLElement) => {
            const ctx = getEditorOperationContext();
            if (ctx) changeDirection(el, ctx);
        },
        onToggleSync: (el: HTMLElement) => {
            const ctx = getEditorOperationContext();
            if (ctx) toggleSyncEditing(el, ctx);
        },
        onSetColored: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setColored(el, ctx);
        },
        onSetBridge: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setBridge(el, ctx);
        },
        onSetImage: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setImage(el, ctx);
        },
        onSetBlack: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setBlack(el, ctx);
        },
        onSetVerticalLine: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setVerticalLine(el, ctx);
        },
        onSetHorizontalLine: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setHorizontalLine(el, ctx);
        },
        onSetKnown: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setKnown(el, ctx);
        },
        onSave: () => savePuzzle(),
    });

    const puzzleEl = document.getElementById('rejtveny');
    if (puzzleEl) {
        session.cellEvents.attachToCells(puzzleEl);
    }
}

/**
 * Initialize the puzzle solver.
 *
 * @param elementId - The container element ID
 * @param solutionId - The solution ID to load
 */
export function initPuzzleSolver(elementId: string, solutionId: number): void {
    initPuzzleBase(false, elementId, solutionId);
}

/**
 * Load saved solution state
 */
function loadSolutionState(solutionId: number): void {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `/fejtes?fejtesID=${solutionId}`, false);
    xhr.send();

    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
            xhr.responseText,
            'application/xml',
        );

        // Load description
        const descEl = xmlDoc.getElementsByTagName('leírás')[0];
        const descContainer = document.getElementById('rejtveny_leiras');
        if (descEl && descContainer) {
            descContainer.innerHTML = descEl.textContent ?? '';
        }

        // Load column values
        const columns = xmlDoc.getElementsByTagName('oszlop');
        for (let i = 0; i < columns.length; i++) {
            const colEl = document.getElementById(`oszlop-${i}`);
            if (colEl) {
                colEl.innerHTML = columns[i].textContent ?? '';
            }
        }
    } catch (error) {
        console.error('Error loading solution state:', error);
    }
}
